/**
 * Per-payment receipt templates + print helper.
 *
 * Two formats are supported:
 *   • `thermal` — 80mm thermal-printer slip (compact, monospace).
 *   • `a4`     — Full A4 letterhead receipt.
 *
 * Call `printPaymentReceipt(payload, { format })` to open a print window with
 * the rendered HTML. `buildReceiptHtml(payload, { format })` returns the HTML
 * string (handy for previews or PDF jobs).
 *
 * Auto-print preference is read from localStorage(`autoPrintReceipts`) — see
 * the Settings page toggle. When auto-print is on, the print window auto-fires
 * `window.print()`; otherwise the operator hits Cmd/Ctrl+P manually.
 *
 * Payload shape (all fields optional unless noted):
 *   {
 *     dealership: { name, address, gstin, state_name, phone, email, logo_url },
 *     receipt_no, paid_on (ISO date), now (ISO datetime),
 *     customer: { name, phone, gstin },
 *     vehicle:  { make, model, variant, color, vin, dealer_code },
 *     sale:     { invoice_no, on_road_price },
 *     line:     { method, amount, reference, paid_on, surcharge_pct,
 *                 surcharge_amount, platform_name, _reconciled },
 *     outstanding_after: number,   // optional — shown when > 0
 *     received_by: string,         // staff / cashier name
 *   }
 */
import { fmtMoney } from "@/lib/format";

const AUTO_PRINT_KEY = "autoPrintReceipts";

export const getAutoPrintReceipts = () => {
  try {
    return localStorage.getItem(AUTO_PRINT_KEY) === "1";
  } catch {
    return false;
  }
};

export const setAutoPrintReceipts = (v) => {
  try {
    localStorage.setItem(AUTO_PRINT_KEY, v ? "1" : "0");
  } catch {
    /* private mode — silently ignore */
  }
};

// ── helpers ─────────────────────────────────────────────────────────────────
const esc = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return (
    d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) +
    " · " +
    d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false })
  );
};

// Indian English number-to-words for currency amounts (paise rounded to whole rupees).
const ones = [
  "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
  "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
  "Seventeen", "Eighteen", "Nineteen",
];
const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

const twoDigit = (n) => {
  if (n < 20) return ones[n];
  return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
};

const threeDigit = (n) => {
  if (n === 0) return "";
  if (n < 100) return twoDigit(n);
  return ones[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + twoDigit(n % 100) : "");
};

export function rupeesInWords(amount) {
  const n = Math.round(Number(amount) || 0);
  if (n === 0) return "Zero only";
  const crore = Math.floor(n / 10000000);
  const lakh = Math.floor((n % 10000000) / 100000);
  const thousand = Math.floor((n % 100000) / 1000);
  const remainder = n % 1000;
  const parts = [];
  if (crore) parts.push(twoDigit(crore) + " Crore");
  if (lakh) parts.push(twoDigit(lakh) + " Lakh");
  if (thousand) parts.push(twoDigit(thousand) + " Thousand");
  if (remainder) parts.push(threeDigit(remainder));
  return "Rupees " + parts.join(" ").trim() + " only";
}

// Friendly labels for payment methods on the receipt.
const METHOD_LABEL = {
  cash: "Cash",
  upi: "UPI",
  card: "Card",
  online: "Online / NEFT",
  platform: "Platform",
};

const methodLabel = (line) => {
  const base = METHOD_LABEL[(line.method || "").toLowerCase()] || (line.method || "—");
  if (line.method === "platform" && line.platform_name) {
    return `${base} · ${line.platform_name}`;
  }
  return base;
};

// ── thermal 80mm template ───────────────────────────────────────────────────
const thermalCss = `
  @page { size: 80mm auto; margin: 0; }
  * { box-sizing: border-box; }
  body { margin: 0; padding: 8px 6px; background: #fff; font: 11px/1.4 'Courier New', 'JetBrains Mono', monospace; color: #111; width: 80mm; }
  .biz { text-align: center; font-weight: 700; font-size: 12px; letter-spacing: 0.04em; text-transform: uppercase; }
  .biz-sub { text-align: center; font-size: 9px; color: #444; line-height: 1.3; margin: 2px 0 6px; }
  hr { border: 0; border-top: 1px dashed #777; margin: 6px 0; }
  .title { text-align: center; font-size: 11px; font-weight: 700; letter-spacing: 0.08em; }
  .rcpt-no { text-align: center; font-size: 11px; font-weight: 700; margin: 3px 0; }
  .row { display: flex; justify-content: space-between; gap: 8px; font-size: 10px; padding: 1px 0; }
  .row span:first-child { color: #555; }
  .amt-box { text-align: center; border: 1px solid #111; padding: 6px 4px; margin: 8px 0 4px; }
  .amt-box .amt { font-size: 20px; font-weight: 800; letter-spacing: -0.02em; }
  .amt-box .lbl { font-size: 8px; letter-spacing: 0.25em; text-transform: uppercase; color: #555; }
  .words { font-size: 9px; text-align: center; font-style: italic; color: #555; margin-bottom: 4px; }
  .balance { background: #fff7eb; border: 1px dashed #b67100; padding: 5px 6px; margin: 6px 0; text-align: center; font-size: 10px; }
  .balance .b-amt { font-weight: 700; color: #b67100; font-size: 13px; }
  .footer { text-align: center; font-size: 8px; color: #555; margin-top: 8px; line-height: 1.4; }
  .sig { font-size: 9px; display: flex; justify-content: space-between; margin-top: 12px; }
  .sig span { border-top: 1px solid #555; padding-top: 2px; width: 45%; text-align: center; color: #555; }
  .badge { display: inline-block; border: 1px solid #b67100; color: #b67100; padding: 1px 4px; font-size: 8px; letter-spacing: 0.15em; text-transform: uppercase; }
`;

function thermalBody(p) {
  const d = p.dealership || {};
  const c = p.customer || {};
  const v = p.vehicle || {};
  const s = p.sale || {};
  // Accept either `line` (single tender) or `lines[]` (consolidated sale receipt).
  const lines = Array.isArray(p.lines) && p.lines.length ? p.lines : (p.line ? [p.line] : []);
  const totalAmount = lines.reduce((a, l) => a + (Number(l.amount) || 0), 0);
  const isConsolidated = lines.length > 1;
  const headerTitle = isConsolidated ? "SALE RECEIPT" : "PAYMENT RECEIPT";
  // For single-tender prints, expose Method / Ref / Surcharge in the body.
  // For consolidated prints, surface every tender in a compact list.
  const single = !isConsolidated ? (lines[0] || {}) : null;
  const reconciledTag = single && single._reconciled
    ? `<div style="text-align:center; margin: 2px 0 4px;"><span class="badge">Reconciled</span></div>`
    : "";
  const reprintTag = p.reprint
    ? `<div style="text-align:center; margin: 2px 0 4px;"><span class="badge" style="border-color:#a30000;color:#a30000;">REPRINT · ${esc(fmtDateTime(p.reprint_at || new Date().toISOString()))}</span></div>`
    : "";

  const tendersBlock = isConsolidated
    ? `
    <hr/>
    <div class="row" style="font-weight:700;"><span>Tenders</span><span>${lines.length}</span></div>
    ${lines.map((l) => {
      const surch = Number(l.surcharge_amount) || 0;
      return `<div class="row"><span>${esc(methodLabel(l))}${l.reference ? ` · ${esc(l.reference)}` : ""}${surch > 0 ? ` (+${esc(fmtMoney(surch))} surch)` : ""}</span><span>${esc(fmtMoney(l.amount || 0))}</span></div>`;
    }).join("")}
  `
    : (single ? `
    <hr/>
    <div class="row"><span>Method</span><span>${esc(methodLabel(single))}</span></div>
    ${single.reference ? `<div class="row"><span>Ref</span><span>${esc(single.reference)}</span></div>` : ""}
    ${(Number(single.surcharge_amount) || 0) > 0 ? `<div class="row"><span>Surcharge</span><span>${esc(fmtMoney(single.surcharge_amount))} (${esc(single.surcharge_pct || 0)}%)</span></div>` : ""}
  ` : "");

  const dateForHeader = p.now || (single && single.paid_on) || new Date().toISOString();

  return `
    <div class="biz">${esc(d.name || "Dealership")}</div>
    <div class="biz-sub">${esc(d.address || "")}${d.gstin ? `<br/>GSTIN ${esc(d.gstin)}` : ""}${d.phone ? `<br/>${esc(d.phone)}` : ""}</div>
    <hr/>
    <div class="title">${headerTitle}</div>
    <div class="rcpt-no">${esc(p.receipt_no || "—")}</div>
    ${reconciledTag}
    ${reprintTag}
    <div class="row"><span>Date</span><span>${esc(fmtDateTime(dateForHeader))}</span></div>
    <hr/>
    <div class="row"><span>Customer</span><span>${esc(c.name || c.full_name || "—")}</span></div>
    ${c.phone ? `<div class="row"><span>Phone</span><span>${esc(c.phone)}</span></div>` : ""}
    ${v.package_name ? `<div class="row"><span>Vehicle</span><span>${esc([v.package_name, v.variant].filter(Boolean).join(" "))}</span></div>` : ""}
    ${v.vin ? `<div class="row"><span>VIN</span><span>${esc(v.vin)}</span></div>` : ""}
    ${s.invoice_no ? `<div class="row"><span>Invoice</span><span>${esc(s.invoice_no)}</span></div>` : ""}
    ${tendersBlock}
    <div class="amt-box">
      <div class="amt">${esc(fmtMoney(totalAmount))}</div>
      <div class="lbl">${isConsolidated ? "Total received" : "Amount received"}</div>
    </div>
    <div class="words">${esc(rupeesInWords(totalAmount))}</div>
    ${
      p.outstanding_after !== undefined && Number(p.outstanding_after) > 0.01
        ? `<div class="balance">Outstanding after this payment<br/><span class="b-amt">${esc(fmtMoney(p.outstanding_after))}</span></div>`
        : ""
    }
    <div class="sig">
      <span>Received by · ${esc(p.received_by || "—")}</span>
      <span>Customer sign</span>
    </div>
    <div class="footer">
      Thank you for choosing ${esc(d.name || "us")}.<br/>
      ${d.phone ? esc(d.phone) : ""}${d.email ? ` · ${esc(d.email)}` : ""}
    </div>
  `;
}

// ── A4 letterhead template ──────────────────────────────────────────────────
const a4Css = `
  @page { size: A4; margin: 18mm 16mm; }
  * { box-sizing: border-box; }
  body { margin: 0; padding: 0; background: #fff; font: 12px/1.5 'Helvetica', 'Arial', sans-serif; color: #111; }
  .a4 { position: relative; padding-bottom: 16px; }
  .a4-head { display: flex; gap: 16px; padding-bottom: 14px; border-bottom: 2px solid #111; align-items: flex-start; }
  .a4-head img.logo { height: 56px; max-width: 120px; object-fit: contain; }
  .biz { font: 700 18px/1.2 'Helvetica', sans-serif; letter-spacing: -0.01em; }
  .biz-sub { color: #555; font-size: 11px; line-height: 1.45; margin-top: 2px; }
  .biz-gstin { color: #555; font-size: 11px; margin-top: 4px; }
  .ribbon { position: absolute; top: 0; right: 0; text-align: right; }
  .label { font: 700 9px/1 'Courier New', monospace; letter-spacing: 0.3em; color: #888; }
  .ribbon .num { font: 700 16px/1.2 'Courier New', monospace; color: #111; margin-top: 4px; }
  .ribbon .when { font-size: 10px; color: #555; margin-top: 2px; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin: 18px 0; }
  .grid h6 { margin: 0 0 6px; font: 700 9px/1 'Courier New', monospace; letter-spacing: 0.25em; text-transform: uppercase; color: #888; }
  .grid .row { display: flex; justify-content: space-between; gap: 12px; padding: 2px 0; font-size: 11px; }
  .grid .row span:first-child { color: #555; }
  .amt-block { border: 2px solid #111; padding: 14px 16px; display: flex; justify-content: space-between; align-items: center; }
  .amt-block .lbl { font: 700 9px/1 'Courier New', monospace; letter-spacing: 0.3em; text-transform: uppercase; color: #888; }
  .amt-block .amt { font: 800 32px/1 'Helvetica', sans-serif; letter-spacing: -0.02em; }
  .words { font-style: italic; font-size: 11px; color: #555; margin: 6px 0 14px; }
  .tenders-tbl { width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 6px; }
  .tenders-tbl th, .tenders-tbl td { text-align: left; padding: 6px 8px; border-bottom: 1px solid #eee; }
  .tenders-tbl th { font: 700 9px/1 'Courier New', monospace; letter-spacing: 0.15em; color: #888; background: #fafafa; }
  .tenders-tbl td.right, .tenders-tbl th.right { text-align: right; }
  .balance { margin-top: 16px; padding: 10px 14px; background: #fff7eb; border: 1px dashed #b67100; display: flex; justify-content: space-between; align-items: center; font-size: 12px; }
  .balance .b-amt { font-weight: 700; color: #b67100; font-size: 15px; }
  .footer { margin-top: 24px; display: flex; justify-content: space-between; align-items: flex-end; font-size: 10px; color: #666; }
  .sig { width: 180px; border-top: 1px solid #555; padding-top: 4px; text-align: center; font-size: 10px; color: #555; }
  .badge { display: inline-block; border: 1px solid #b67100; color: #b67100; padding: 2px 8px; font-size: 9px; letter-spacing: 0.15em; text-transform: uppercase; margin-left: 8px; vertical-align: middle; }
`;

function a4Body(p) {
  const d = p.dealership || {};
  const c = p.customer || {};
  const v = p.vehicle || {};
  const s = p.sale || {};
  // Same flex contract as the thermal template — accept `line` or `lines[]`.
  const lines = Array.isArray(p.lines) && p.lines.length ? p.lines : (p.line ? [p.line] : []);
  const totalAmount = lines.reduce((a, l) => a + (Number(l.amount) || 0), 0);
  const isConsolidated = lines.length > 1;
  const headerLabel = isConsolidated ? "Total received" : "Amount received";
  const reconciledBadge = (!isConsolidated && lines[0] && lines[0]._reconciled)
    ? `<span class="badge">Reconciled</span>` : "";
  const reprintBadge = p.reprint
    ? `<span class="badge" style="border-color:#a30000;color:#a30000;">REPRINT · ${esc(fmtDateTime(p.reprint_at || new Date().toISOString()))}</span>` : "";
  const logoHtml = d.logo_url
    ? `<img class="logo" src="${esc(d.logo_url)}" alt="" />`
    : "";
  const dateForHeader = p.now || (lines[0] && lines[0].paid_on) || new Date().toISOString();

  return `
    <div class="a4">
      <div class="a4-head">
        ${logoHtml}
        <div>
          <div class="biz">${esc(d.name || "Dealership")}${reconciledBadge}${reprintBadge}</div>
          <div class="biz-sub">${esc(d.address || "")}</div>
          <div class="biz-gstin">
            ${d.gstin ? `GSTIN ${esc(d.gstin)}` : ""}
            ${d.phone ? ` · ${esc(d.phone)}` : ""}
            ${d.email ? ` · ${esc(d.email)}` : ""}
          </div>
        </div>
      </div>
      <div class="ribbon">
        <div class="label">${isConsolidated ? "Sale Receipt #" : "Receipt #"}</div>
        <div class="num">${esc(p.receipt_no || "—")}</div>
        <div class="when">${esc(fmtDateTime(dateForHeader))}</div>
      </div>

      <div class="grid">
        <div>
          <h6>Received from</h6>
          <div class="row"><span>Customer</span><span>${esc(c.name || c.full_name || "—")}</span></div>
          ${c.phone ? `<div class="row"><span>Phone</span><span>${esc(c.phone)}</span></div>` : ""}
          ${(c.gst_number || c.gstin) ? `<div class="row"><span>GSTIN</span><span>${esc(c.gst_number || c.gstin)}</span></div>` : ""}
        </div>
        <div>
          <h6>Vehicle / Invoice</h6>
          ${
            v.package_name
              ? `<div class="row"><span>Model</span><span>${esc([v.package_name, v.variant, v.color].filter(Boolean).join(" · "))}</span></div>`
              : ""
          }
          ${v.vin ? `<div class="row"><span>VIN</span><span>${esc(v.vin)}</span></div>` : ""}
          ${v.dealer_code ? `<div class="row"><span>Dealer code</span><span>${esc(v.dealer_code)}</span></div>` : ""}
          ${s.invoice_no ? `<div class="row"><span>Invoice</span><span>${esc(s.invoice_no)}</span></div>` : ""}
        </div>
      </div>

      <div class="amt-block">
        <div class="lbl">${headerLabel}</div>
        <div class="amt">${esc(fmtMoney(totalAmount))}</div>
      </div>
      <div class="words">${esc(rupeesInWords(totalAmount))}</div>

      <table class="tenders-tbl">
        <thead><tr><th>Method</th><th>Reference</th><th>Date</th><th class="right">Surcharge</th><th class="right">Amount</th></tr></thead>
        <tbody>
          ${lines.map((l) => {
            const surch = Number(l.surcharge_amount) || 0;
            return `<tr>
              <td>${esc(methodLabel(l))}</td>
              <td>${esc(l.reference || "—")}</td>
              <td>${esc((l.paid_on || "").slice(0, 10) || "—")}</td>
              <td class="right">${surch > 0 ? esc(fmtMoney(surch)) + ` (${esc(l.surcharge_pct || 0)}%)` : "—"}</td>
              <td class="right">${esc(fmtMoney(l.amount || 0))}</td>
            </tr>`;
          }).join("")}
          ${isConsolidated ? `<tr style="font-weight:700; background:#fafafa;">
            <td colspan="4" style="text-align:right;">Total</td>
            <td class="right">${esc(fmtMoney(totalAmount))}</td>
          </tr>` : ""}
        </tbody>
      </table>

      ${
        p.outstanding_after !== undefined && Number(p.outstanding_after) > 0.01
          ? `<div class="balance"><span>Outstanding after this payment</span><span class="b-amt">${esc(fmtMoney(p.outstanding_after))}</span></div>`
          : ""
      }

      <div class="footer">
        <div>
          Received by · <strong>${esc(p.received_by || "—")}</strong><br/>
          Computer-generated receipt — valid without signature
        </div>
        <div class="sig">Customer signature</div>
      </div>
    </div>
  `;
}

// ── public API ──────────────────────────────────────────────────────────────
export function buildReceiptHtml(payload, { format = "thermal" } = {}) {
  const isThermal = format === "thermal";
  const css = isThermal ? thermalCss : a4Css;
  const body = isThermal ? thermalBody(payload) : a4Body(payload);
  const title = `Receipt ${payload.receipt_no || ""}`.trim();
  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>${esc(title)}</title>
    <style>${css}</style>
  </head>
  <body>${body}</body>
</html>`;
}

/**
 * Open a new window with the rendered receipt and (optionally) auto-fire
 * `window.print()`. Returns the opened Window handle, or `null` if pop-ups
 * were blocked.
 */
export function printPaymentReceipt(payload, { format = "thermal", autoPrint } = {}) {
  const html = buildReceiptHtml(payload, { format });
  const w = window.open("", "_blank", "width=720,height=900");
  if (!w) return null;
  w.document.open();
  w.document.write(html);
  w.document.close();
  const shouldPrint = autoPrint ?? getAutoPrintReceipts();
  if (shouldPrint) {
    // Give the new window a tick to paint before kicking off the print dialog.
    w.onload = () => {
      try {
        w.focus();
        w.print();
      } catch {
        /* user can hit Cmd/Ctrl+P manually */
      }
    };
  }
  return w;
}

/**
 * Consolidated sale receipt — ONE slip per sale showing every captured tender
 * combined (including payments rolled over from a booking conversion).
 * Excludes `_reconciled` post-sale collections so they print on their own
 * separate receipt via `ReconcileDueButton`.
 */
export function printSaleReceipt(sale, dealership, { format = "a4", autoPrint, receivedBy } = {}) {
  const allLines = Array.isArray(sale?.payment_lines) ? sale.payment_lines : [];
  const lines = allLines.filter((l) => !l._reconciled);
  if (lines.length === 0) return null;
  // Sale receipt # = first captured tender's receipt_no (they all share the
  // same RC/<branch>/<FY>/<seq> prefix, just incrementing seq per line).
  const receiptNo = lines.find((l) => l.receipt_no)?.receipt_no || "";
  return printPaymentReceipt(
    {
      dealership: dealership || {},
      receipt_no: receiptNo,
      now: new Date().toISOString(),
      lines,
      customer: sale.customer || {},
      vehicle: sale.vehicle || {},
      sale: {
        invoice_no: sale.invoice_no || (sale.id ? sale.id.slice(0, 8).toUpperCase() : ""),
        on_road_price: sale.on_road_price,
      },
      outstanding_after: Number(sale.due) || 0,
      received_by: receivedBy || sale.salesperson_name || "",
    },
    { format, autoPrint },
  );
}


/**
 * Print N thermal receipts stacked into ONE print window — used by the
 * Bulk Sale flow so the operator hands the customer one slip per vehicle
 * after creating multiple sales in one transaction.
 *
 * Each payload follows the same shape as `printPaymentReceipt`. We render
 * each body inside its own div with a CSS page-break so the printer cuts
 * cleanly between slips.
 */
export function printPaymentReceiptBatch(payloads, { autoPrint } = {}) {
  if (!Array.isArray(payloads) || payloads.length === 0) return null;
  const bodies = payloads
    .map((p) => `<div class="slip">${thermalBody(p)}</div>`)
    .join("");
  const css = `${thermalCss}
    .slip { page-break-after: always; }
    .slip:last-child { page-break-after: auto; }
  `;
  const html = `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>Bulk Receipts (${payloads.length})</title>
    <style>${css}</style>
  </head>
  <body>${bodies}</body>
</html>`;
  const w = window.open("", "_blank", "width=720,height=900");
  if (!w) return null;
  w.document.open();
  w.document.write(html);
  w.document.close();
  const shouldPrint = autoPrint ?? getAutoPrintReceipts();
  if (shouldPrint) {
    w.onload = () => {
      try {
        w.focus();
        w.print();
      } catch {
        /* user can hit Cmd/Ctrl+P manually */
      }
    };
  }
  return w;
}
